import type { ChatConnectionNotice } from '../libs/chat-connection'

import { useLocalStorage } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { chatConnectionRoute, resolveChatConnectionPhase } from '../libs/chat-connection'
import { useConsciousnessStore } from '../stores/modules/consciousness'
import { useProviderConfigStore } from '../stores/providers/config'
import { useProviderStore } from '../stores/providers/provider'

export const chatConnectionNoticeKey = 'settings/chat-connection/notice'

export function useChatConnectionNotice() {
  return useLocalStorage<ChatConnectionNotice | null>(chatConnectionNoticeKey, null)
}

/** Reads the shared chat provider, model, and verification state. */
export function useChatConnection() {
  const consciousness = useConsciousnessStore()
  const providerConfig = useProviderConfigStore()
  const providers = useProviderStore()
  const { activeProvider, activeModel } = storeToRefs(consciousness)
  const notice = useChatConnectionNotice()

  const providerStatus = computed(() => {
    const providerId = activeProvider.value
    if (!providerId)
      return undefined
    return providerConfig.providers[providerId]?.status
  })

  const phase = computed(() => resolveChatConnectionPhase({
    activeProviderId: activeProvider.value,
    activeModelId: activeModel.value,
    providerStatus: providerStatus.value,
  }))

  const providerLabel = computed(() => {
    const providerId = activeProvider.value
    if (!providerId)
      return ''
    return providers.getLocalizedProviderName(providerId) || providerId
  })

  const errorText = computed(() => {
    if (phase.value !== 'failed')
      return ''
    return notice.value?.providerId === activeProvider.value ? notice.value.error : ''
  })

  const checkedAt = computed(() => {
    if (notice.value?.providerId !== activeProvider.value)
      return ''
    return notice.value.checkedAt
  })

  const route = computed(() => chatConnectionRoute(phase.value))

  return {
    phase,
    providerLabel,
    modelLabel: activeModel,
    errorText,
    checkedAt,
    route,
    notice,
  }
}
